import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN } from "../../theme";
import { useBreakpoint } from "../../shell/LearnShell";
import { ConfettiBurst } from "../../feedback/Celebration";
import { fitTiles, useCompleteOnce } from "./engineUtils";
import { Art, Counter, IconSparkle, ROOT_STYLE, SparkleBurst, StickerTile, fillTemplate, frost, normalizeItems, tileMultForCount, tileSize, useArenaMetrics, useLetterAccent, useTileFeedback } from "./tapUtils";

/**
 * Tap each closed spot to discover a word. The spot flips open to show the
 * art; the newest discovery is shown large with its reveal line.
 * Completes once every spot is opened.
 */
export default function DiscoveryGame({ letter, config = {}, speak, sfx, onComplete, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { accent, deep } = useLetterAccent(letter);
  const bp = useBreakpoint();
  const compact = bp === "xs";
  const rootRef = useRef(null);
  const { w, h, tile } = useArenaMetrics(rootRef);
  const items = useMemo(() => normalizeItems(config.items || config.objects || config.hotspots || [], letter), [config.items, config.objects, config.hotspots, letter]);
  const [opened, setOpened] = useState(() => new Set());
  const [reveal, setReveal] = useState(null);
  const { pulses, pulseId } = useTileFeedback();
  const done = items.length > 0 && opened.size >= items.length;
  const revealTpl = config.revealTemplate || "{word}!";

  useEffect(() => {
    if (!done) return undefined;
    sfx?.("sparkle");
    const t = setTimeout(finish, reduceMotion ? 250 : 1000);
    return () => clearTimeout(t);
  }, [done, finish, reduceMotion, sfx]);

  const open = (it) => {
    if (opened.has(it.key)) return;
    sfx?.("pop");
    setOpened((s) => new Set(s).add(it.key));
    setReveal(it);
    pulseId(it.key);
    speak?.(fillTemplate(config.revealTemplate || "Look! {word}", it.word || letter));
  };

  // Spots keep a fixed column count from the fit so the grid never reflows
  // mid-game; they shrink (to 0.85 tiles) before anything scrolls.
  const bannerH = tile ? tile * 1.3 + 24 + 8 : 0;
  const fit = fitTiles({
    count: items.length,
    w: w ? w - 8 : 0,
    h: h ? h - bannerH - 12 - (compact ? 30 : 0) : 0,
    tile,
    max: items.length <= 6 ? 1.6 : tileMultForCount(items.length),
    min: 0.85,
    gap: 0.18,
    maxCols: items.length <= 6 ? 3 : items.length <= 8 ? 4 : 5,
  });
  const mult = fit.scale;
  return (
    <div ref={rootRef} style={ROOT_STYLE}>
      <ConfettiBurst active={done} originY={35} count={36} reduceMotion={reduceMotion} />
      <Counter done={opened.size} total={items.length} accent={accent} suffix="found" />
      {/* Reveal banner: the newest discovery, large, with its line. Empty until the first tap. */}
      <div style={{ flexShrink: 0, minHeight: `calc(${tileSize(1.3)} + 24px)`, display: "flex", justifyContent: "center", alignItems: "center", marginBottom: 8, marginTop: compact ? 30 : 0 }}>
        <AnimatePresence mode="wait">
          {reveal && (
            <motion.div
              key={reveal.key}
              initial={reduceMotion ? false : { y: 16, scale: 0.9, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={reduceMotion ? undefined : { y: -10, opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 26 }}
              style={frost({ padding: "8px 20px 8px 10px", display: "flex", alignItems: "center", gap: 14, maxWidth: "100%", boxSizing: "border-box", borderColor: `${accent}44` })}
            >
              <span style={{ width: tileSize(1.3), height: tileSize(1.3), borderRadius: "calc(var(--tile, 72px) * 0.28)", background: `${accent}14`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, position: "relative" }}>
                <Art letter={letter} assetName={reveal.assetName} word={reveal.word} alt="" style={{ width: "86%", height: "86%" }} />
                <SparkleBurst trigger={pulses[reveal.key] || 0} color={accent} reduceMotion={reduceMotion} distance={60} />
              </span>
              <span style={{ fontSize: "clamp(22px, calc(var(--tile, 72px) * 0.36), 32px)", fontWeight: 700, color: deep, lineHeight: 1.15 }}>
                {fillTemplate(revealTpl, reveal.word)}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: fit.fits ? "hidden" : "auto", display: "flex", alignItems: "center", justifyContent: "center", padding: "4px 2px" }}>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${fit.cols}, max-content)`, justifyContent: "center", gap: "calc(var(--tile, 72px) * 0.18)", width: "100%" }}>
          {items.map((it, i) => {
            const isOpen = opened.has(it.key);
            return (
              <div key={it.key} style={{ perspective: 800, display: "flex", justifyContent: "center" }}>
                <motion.div
                  animate={{ rotateY: isOpen ? 0 : 180 }}
                  initial={false}
                  transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 260, damping: 22 }}
                  style={{ transformStyle: "preserve-3d", position: "relative" }}
                >
                  <div style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}>
                    <StickerTile
                      letter={letter}
                      assetName={it.assetName}
                      word={it.word}
                      label={it.word}
                      size={mult}
                      index={i}
                      accent={accent}
                      reduceMotion={reduceMotion}
                      state={isOpen ? "done" : "idle"}
                      disabled
                      ariaLabel={it.word}
                      aria-hidden={!isOpen}
                      tabIndex={-1}
                      style={{ opacity: 1, background: "#fff" }}
                      badge={<span />}
                    />
                  </div>
                  <div style={{ position: "absolute", inset: 0, backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
                    <StickerTile
                      size={mult}
                      index={i}
                      accent={accent}
                      reduceMotion={reduceMotion}
                      label={it.raw?.title || undefined}
                      ariaLabel={it.raw?.title || `Spot ${i + 1}`}
                      aria-hidden={isOpen}
                      tabIndex={isOpen ? -1 : 0}
                      onTap={() => open(it)}
                      disabled={isOpen}
                      style={{ background: `linear-gradient(160deg, ${accent} 0%, ${deep} 100%)`, color: "#fff", borderColor: "rgba(255,255,255,0.7)", boxShadow: `${LEARN.shadow.sticker(deep)}, ${LEARN.shadow.card}`, height: "100%" }}
                    >
                      <motion.span
                        animate={reduceMotion || isOpen ? { scale: 1, rotate: 0 } : { scale: [1, 1.15, 1], rotate: [0, 12, 0] }}
                        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: (i % 4) * 0.35 }}
                        style={{ display: "inline-flex" }}
                      >
                        <IconSparkle size={Math.round(34 * mult)} />
                      </motion.span>
                    </StickerTile>
                  </div>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
